import * as React from "react";
import ReactDOM from "react-dom"; // Added for portals
import {
    Chart,
    ChartSeries,
    ChartSeriesItem,
    ChartCategoryAxis,
    ChartCategoryAxisItem,
    ChartTitle,
    ChartLegend,
    exportVisual,
    ChartValueAxis,
    ChartValueAxisItem,
    ChartSeriesDefaults,
} from "@progress/kendo-react-charts";
import configData from "../../config.json";
import { GetSession, GetToken } from "../../components/Session";
import { ExpansionPanel, ExpansionPanelContent } from "@progress/kendo-react-layout";
import { Reveal } from "@progress/kendo-react-animation";
import DashboardCards from "./DashboardCards";
import "hammerjs";
import { exportPDF } from "@progress/kendo-drawing";
import { saveAs } from "@progress/kendo-file-saver";
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { GetCloudLogin } from "../../WeighBridge/Helpers";

const cardStyle3D = {
    borderRadius: "10px",
    boxShadow:
        "0 6px 15px rgba(0, 0, 0, 0.25), inset 0 -3px 6px rgba(255, 255, 255, 0.3)",
    backgroundColor: "#fff",
};

const buttonStaticStyle = {
    borderRadius: "6px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
    cursor: "pointer",
    backgroundColor: "#fff",
    border: "1px solid #ccc",
    padding: "6px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
};

const modalStyles = {
    overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.6)",
        zIndex: 10000,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "20px",
    },
    content: {
        backgroundColor: "white",
        borderRadius: "8px",
        width: "95vw",
        height: "95vh",
        padding: "20px",
        position: "relative",
        boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        perspective: "1000px",
    },
    closeBtnTopRight: {
        position: "absolute",
        top: "10px",
        right: "15px",
        fontSize: "28px",
        border: "none",
        background: "none",
        cursor: "pointer",
        color: "#333",
    },
    closeBtnBottom: {
        marginTop: "auto",
        alignSelf: "center",
        padding: "10px 25px",
        fontSize: "16px",
        borderRadius: "5px",
        border: "1px solid #333",
        background: "white",
        cursor: "pointer",
        color: "#333",
        transition: "background-color 0.2s ease",
    },
};

const RealTimeTrend = () => {
    const [categories, setCategories] = React.useState(null);
    const [sales, setSales] = React.useState(null);
    const [productPerformance, setProductPerformance] = React.useState(null);
    const [trucks, setTrucks] = React.useState(null);
    const [tonnage, setTonnage] = React.useState(null);
    const [expanded, setExpanded] = React.useState(false);
    const [hours, setHours] = React.useState([]);
    const [sites, setSites] = React.useState([]);
    const [trucksOnSite, setTrucksOnSite] = React.useState(null);
    const [modalChart, setModalChart] = React.useState(null);
    const sale = React.useRef(null);
    const product = React.useRef(null);
    const total = React.useRef(null);
    const site = React.useRef(null);
    const transCount = React.useRef("");
    const timer = React.useRef(null);
    const cloudToken = React.useRef("");
    const [valueAxisMax, setValueAxisMax] = React.useState(2);

    const valueAxisLabels = { step: 2 };

    React.useEffect(() => {
        GetCloudLogin((cred) => {
            let token = cred === "" ? cred : cred.token;
            cloudToken.current = token;
            const init = {
                method: "GET",
                accept: "application/json",
                headers: { Authorization: "Bearer " + GetToken() },
            };
            let url = `${configData.SERVER_URL}apiv7/settings`;
            fetch(url, init)
                .then((response) => response.json())
                .then((d) => {
                    if (d.length > 0) {
                        let updateRate = GetSetting(d, "RealTimeDashRefresh") * 1000;
                        RefreshData();
                        timer.current = setInterval(RefreshData, updateRate);
                    }
                });
        });
        return () => {
            if (timer.current !== null) clearInterval(timer.current);
        };
    }, []);

    const GetSetting = (s, name) => {
        if (!s) return false;
        let st = s.find((obj) => obj.name === name);
        if (!st) return undefined;
        let v = String(st.value);
        switch (st.type) {
            case 0:
                v = v.toLowerCase() === "true";
                break;
            case 1:
                v = parseInt(v);
                break;
            case 2:
                v = parseFloat(v);
                break;
        }
        return v;
    };

    const RefreshData = () => {
        const init = {
            method: "GET",
            accept: "application/json",
            headers: {
                Authorization:
                    "Bearer " +
                    (cloudToken.current === null || cloudToken.current === ""
                        ? GetToken()
                        : cloudToken.current),
            },
        };
        const timeOffset = new Date().getTimezoneOffset() / 60;
        let sess = GetSession();
        const baseUrl =
            cloudToken.current !== undefined && cloudToken.current !== ""
                ? sess.CloudURL
                : configData.SERVER_URL;
        let url = `${baseUrl}apiV7/dashboard/gettransactioncount?siteId=${sess.SiteID}`;

        fetch(url, init)
            .then((response) => response.text())
            .then((d) => {
                if (d !== transCount.current) {
                    transCount.current = d;
                    url = `${baseUrl}apiV7/dashboard/getrealtime?offset=${timeOffset}&siteId=${sess.SiteID}`;
                    fetch(url, init)
                        .then((response) => response.json())
                        .then((d) => {
                            setHours(d.hours);
                            setSales(d.sales);
                            if (d.maxSale > 1.8 && d.maxSale * 1.2 !== valueAxisMax)
                                setValueAxisMax(d.maxSale * 1.2);
                            setProductPerformance(d.productPerformance);
                            setTonnage(d.tonnage);
                            setTrucks(d.trucks);
                            setSites(d.siteNames);
                            if (d.trucksOnSite !== null) {
                                let ts = [...d.trucksOnSite];
                                ts.forEach((s) => {
                                    s.timeIn = new Date(s.timeIn + "Z").toLocaleTimeString("en-AU");
                                });
                                setTrucksOnSite(ts);
                            }
                            setCategories(d.productNames);
                        });
                }
            });
    };

    const onImageExportClick = (e) => {
        const chart = e.currentTarget.name;
        let chartVisual = null;
        if (chart === "sales") chartVisual = sale.current && exportVisual(sale.current);
        else if (chart === "product")
            chartVisual = product.current && exportVisual(product.current);
        else if (chart === "total")
            chartVisual = total.current && exportVisual(total.current);
        else chartVisual = site.current && exportVisual(site.current);

        if (chartVisual) {
            exportPDF(chartVisual, {
                paperSize: "A4",
                landscape: true,
            }).then((dataURI) => saveAs(dataURI, "RealtimeData.pdf"));
        }
    };

    const renderModalChart = () => {
        if (!modalChart) return null;

        const commonProps = {
            style: {
                height: "85vh",
                width: "100%",
                filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.2))",
                borderRadius: "10px",
            },
            transitions: false,
        };

        switch (modalChart) {
            case "sales":
                return (
                    <Chart {...commonProps} ref={sale}>
                        <ChartTitle text="Transactions" font="22Pt bold" color="#00008B" />
                        <ChartLegend position="top" orientation="horizontal" />
                        <ChartCategoryAxis>
                            <ChartCategoryAxisItem categories={hours} startAngle={45} />
                        </ChartCategoryAxis>
                        <ChartValueAxis>
                            <ChartValueAxisItem min={0} max={valueAxisMax} labels={valueAxisLabels} />
                        </ChartValueAxis>
                        <ChartSeriesDefaults type="line" />
                        <ChartSeries>
                            {sales.map((item, idx) => (
                                <ChartSeriesItem
                                    key={idx}
                                    type="line"
                                    style="step"
                                    markers={{ visible: false }}
                                    tooltip={{ visible: true }}
                                    data={item.data}
                                    name={item.name}
                                />
                            ))}
                        </ChartSeries>
                    </Chart>
                );
            case "product":
                return (
                    <Chart {...commonProps} ref={product}>
                        <ChartTitle text="Products" font="22Pt bold" color="#00008B" />
                        <ChartLegend visible={false} position="top" orientation="horizontal" />
                        <ChartCategoryAxis>
                            <ChartCategoryAxisItem categories={categories} startAngle={45} />
                        </ChartCategoryAxis>
                        <ChartSeriesDefaults type="column" />
                        <ChartSeries>
                            {productPerformance.map((item, idx) => (
                                <ChartSeriesItem
                                    key={idx}
                                    type="column"
                                    tooltip={{ visible: true }}
                                    data={item.data}
                                    name={item.name}
                                />
                            ))}
                        </ChartSeries>
                    </Chart>
                );
            case "total":
                return (
                    <Chart {...commonProps} ref={total}>
                        <ChartTitle text="Tonnage" font="22Pt bold" color="#00008B" />
                        <ChartLegend position="top" orientation="horizontal" />
                        <ChartCategoryAxis>
                            <ChartCategoryAxisItem categories={hours} startAngle={45} />
                        </ChartCategoryAxis>
                        <ChartSeriesDefaults type="line" style="step" markers={{ visible: false }} />
                        <ChartSeries>
                            {tonnage.map((item, idx) => (
                                <ChartSeriesItem
                                    key={idx}
                                    type="line"
                                    style="step"
                                    markers={{ visible: false }}
                                    tooltip={{ visible: true }}
                                    data={item.data}
                                    name={item.name}
                                />
                            ))}
                        </ChartSeries>
                    </Chart>
                );
            case "site":
                return (
                    <Chart {...commonProps} ref={site}>
                        <ChartTitle text="Trucks on Site" font="22Pt bold" color="#00008B" />
                        <ChartLegend visible={false} position="top" orientation="horizontal" />
                        <ChartCategoryAxis>
                            <ChartCategoryAxisItem categories={sites} startAngle={45} />
                        </ChartCategoryAxis>
                        <ChartSeriesDefaults type="column" />
                        <ChartSeries>
                            {trucks.map((item, idx) => (
                                <ChartSeriesItem
                                    key={idx}
                                    type="column"
                                    tooltip={{ visible: true }}
                                    data={item.data}
                                    name={item.name}
                                />
                            ))}
                        </ChartSeries>
                    </Chart>
                );
            default:
                return null;
        }
    };

    const ExportZoomButton = ({ name, onClick, iconClass, ariaLabel }) => (
        <button
            type="button"
            name={name}
            onClick={onClick}
            aria-label={ariaLabel}
            style={buttonStaticStyle}
            className="k-button k-button-icon"
        >
            <i className={`fa ${iconClass}`} aria-hidden="true" />
        </button>
    );

    const ButtonsGroup = ({ exportName, onExportClick, onZoomClick }) => (
        <div
            style={{
                display: "flex",
                gap: "8px",
                justifyContent: "flex-end",
                marginBottom: 8,
            }}
        >
            <ExportZoomButton
                name={exportName}
                onClick={onExportClick}
                iconClass="fa-download"
                ariaLabel="Export as PDF"
            />
            <ExportZoomButton
                onClick={onZoomClick}
                iconClass="fa-expand"
                ariaLabel="Expand Chart"
            />
        </div>
    );

    return (
        <>
            <ExpansionPanel
                title="Quick View"
                expandIcon="fa fa-plus"
                collapseIcon="fa fa-minus"
                expanded={expanded}
                onAction={(e) => setExpanded(!e.expanded)}
            >
                <Reveal>
                    {expanded && (
                        <ExpansionPanelContent>
                            <DashboardCards period="daily" />
                        </ExpansionPanelContent>
                    )}
                </Reveal>
            </ExpansionPanel>

            <div className="row mb-3">
                {sales && (
                    <div className="col-6 mb-4">
                        <ButtonsGroup
                            exportName="sales"
                            onExportClick={onImageExportClick}
                            onZoomClick={() => setModalChart("sales")}
                        />
                        <div className="k-card" style={cardStyle3D}>
                            <Chart
                                ref={sale}
                                name="sales"
                                style={{
                                    height: 350,
                                    filter: "drop-shadow(0 0 5px rgba(0, 0, 0, 0.15))",
                                    borderRadius: "10px",
                                }}
                                transitions={false}
                            >
                                <ChartTitle text="Transactions" font="22Pt bold" color="#00008B" />
                                <ChartLegend position="top" orientation="horizontal" />
                                <ChartCategoryAxis>
                                    <ChartCategoryAxisItem categories={hours} startAngle={45} />
                                </ChartCategoryAxis>
                                <ChartValueAxis>
                                    <ChartValueAxisItem min={0} max={valueAxisMax} labels={valueAxisLabels} />
                                </ChartValueAxis>
                                <ChartSeriesDefaults type="line" />
                                <ChartSeries>
                                    {sales.map((item, idx) => (
                                        <ChartSeriesItem
                                            key={idx}
                                            type="line"
                                            style="step"
                                            markers={{ visible: false }}
                                            tooltip={{ visible: true }}
                                            data={item.data}
                                            name={item.name}
                                        />
                                    ))}
                                </ChartSeries>
                            </Chart>
                        </div>
                    </div>
                )}

                {productPerformance && (
                    <div className="col-6 mb-4">
                        <ButtonsGroup
                            exportName="product"
                            onExportClick={onImageExportClick}
                            onZoomClick={() => setModalChart("product")}
                        />
                        <div className="k-card" style={cardStyle3D}>
                            <Chart
                                ref={product}
                                style={{
                                    height: 350,
                                    filter: "drop-shadow(0 0 5px rgba(0, 0, 0, 0.15))",
                                    borderRadius: "10px",
                                }}
                                transitions={false}
                            >
                                <ChartTitle text="Products" font="22Pt bold" color="#00008B" />
                                <ChartLegend visible={false} position="top" orientation="horizontal" />
                                <ChartCategoryAxis>
                                    <ChartCategoryAxisItem categories={categories} startAngle={45} />
                                </ChartCategoryAxis>
                                <ChartSeriesDefaults type="column" />
                                <ChartSeries>
                                    {productPerformance.map((item, idx) => (
                                        <ChartSeriesItem
                                            key={idx}
                                            type="column"
                                            tooltip={{ visible: true }}
                                            data={item.data}
                                            name={item.name}
                                        />
                                    ))}
                                </ChartSeries>
                            </Chart>
                        </div>
                    </div>
                )}
            </div>

            <div className="row">
                {tonnage && (
                    <div className="col-6 mb-4">
                        <ButtonsGroup
                            exportName="total"
                            onExportClick={onImageExportClick}
                            onZoomClick={() => setModalChart("total")}
                        />
                        <div className="k-card" style={cardStyle3D}>
                            <Chart
                                ref={total}
                                style={{
                                    height: 350,
                                    filter: "drop-shadow(0 0 5px rgba(0, 0, 0, 0.15))",
                                    borderRadius: "10px",
                                }}
                                transitions={false}
                            >
                                <ChartTitle text="Tonnage" font="22Pt bold" color="#00008B" />
                                <ChartLegend position="top" orientation="horizontal" />
                                <ChartCategoryAxis>
                                    <ChartCategoryAxisItem categories={hours} startAngle={45} />
                                </ChartCategoryAxis>
                                <ChartSeriesDefaults type="line" style="step" markers={{ visible: false }} />
                                <ChartSeries>
                                    {tonnage.map((item, idx) => (
                                        <ChartSeriesItem
                                            key={idx}
                                            type="line"
                                            style="step"
                                            markers={{ visible: false }}
                                            tooltip={{ visible: true }}
                                            data={item.data}
                                            name={item.name}
                                        />
                                    ))}
                                </ChartSeries>
                            </Chart>
                        </div>
                    </div>
                )}

                <div className="col-6 mb-4">
                    {trucks && trucksOnSite === null && (
                        <>
                            <ButtonsGroup
                                exportName="site"
                                onExportClick={onImageExportClick}
                                onZoomClick={() => setModalChart("site")}
                            />
                            <div className="k-card" style={cardStyle3D}>
                                <Chart
                                    ref={site}
                                    style={{
                                        height: 350,
                                        filter: "drop-shadow(0 0 5px rgba(0, 0, 0, 0.15))",
                                        borderRadius: "10px",
                                    }}
                                    transitions={false}
                                >
                                    <ChartTitle text="Trucks on Site" font="22Pt bold" color="#00008B" />
                                    <ChartLegend visible={false} position="top" orientation="horizontal" />
                                    <ChartCategoryAxis>
                                        <ChartCategoryAxisItem categories={sites} startAngle={45} />
                                    </ChartCategoryAxis>
                                    <ChartSeriesDefaults type="column" />
                                    <ChartSeries>
                                        {trucks.map((item, idx) => (
                                            <ChartSeriesItem
                                                key={idx}
                                                type="column"
                                                tooltip={{ visible: true }}
                                                data={item.data}
                                                name={item.name}
                                            />
                                        ))}
                                    </ChartSeries>
                                </Chart>
                            </div>
                        </>
                    )}

                    {trucksOnSite && (
                        <div className="k-card" style={cardStyle3D}>
                            <Grid data={trucksOnSite} style={{ height: "400px" }}>
                                <GridColumn field="rego" title="Rego" />
                                <GridColumn field="direction" title="Direction" />
                                <GridColumn field="totalTare" title="Total Tare" />
                                <GridColumn field="timeIn" title="Time In" />
                                <GridColumn field="timeOnSite" title="Time On Site" />
                            </Grid>
                        </div>
                    )}
                </div>
            </div>

            {modalChart &&
                ReactDOM.createPortal(
                    <div style={modalStyles.overlay} onClick={() => setModalChart(null)}>
                        <div
                            style={modalStyles.content}
                            onClick={(e) => e.stopPropagation()}
                            role="dialog"
                            aria-modal="true"
                        >
                            <button
                                style={modalStyles.closeBtnTopRight}
                                aria-label="Close"
                                onClick={() => setModalChart(null)}
                            >
                                &times;
                            </button>
                            {renderModalChart()}
                            <button
                                style={modalStyles.closeBtnBottom}
                                onClick={() => setModalChart(null)}
                            >
                                Close
                            </button>
                        </div>
                    </div>,
                    document.body
                )}
        </>
    );
};

export default RealTimeTrend;
